import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/authStore';
import { usePreferencesStore } from '@/stores/preferencesStore';
import { login } from '@/services/authApi';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';
import { showToast } from '@/components/Toast/Toast';
import styles from './LoginPage.module.css';

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, login: storeLogin, clearRedirectUrl, redirectUrl } = useAuthStore();
  const { theme } = usePreferencesStore();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Determine which logo to use based on theme
  const logoSrc = theme === 'dark' ? 'logo-cinfo-white.svg' : 'logo-cinfo-color.svg';

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const savedUrl = clearRedirectUrl();
      navigate(savedUrl || '/thematic', { replace: true });
    }
  }, [isAuthenticated, navigate, clearRedirectUrl]);

  // Show message if redirected from protected route
  useEffect(() => {
    if (redirectUrl) {
      showToast('warning', t('auth.sessionExpired'));
    }
  }, [redirectUrl, t]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);

      if (!username.trim()) {
        setError(t('auth.usernameRequired'));
        return;
      }

      if (!password.trim()) {
        setError(t('auth.passwordRequired'));
        return;
      }

      setLoading(true);

      const result = await login({ username: username.trim(), password });

      if (result.ok && result.data) {
        storeLogin(result.data.access_token, username.trim());
        showToast('success', t('auth.loginSuccess'));
        const savedUrl = clearRedirectUrl();
        navigate(savedUrl || '/thematic', { replace: true });
      } else {
        setError(result.error || t('auth.loginFailed'));
      }

      setLoading(false);
    },
    [username, password, t, storeLogin, clearRedirectUrl, navigate]
  );

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <header className={styles.header}>
          <img 
            src={logoSrc} 
            alt="Cinfo" 
            className={styles.logo}
          />
          <h1 className={styles.title}>{t('auth.loginTitle')}</h1>
        </header>

        <form className={styles.form} onSubmit={handleSubmit}>
          {error && (
            <div className={styles.errorBanner} role="alert">
              {error}
            </div>
          )}

          <div className={styles.field}>
            <Input
              label={t('auth.username')}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t('auth.usernamePlaceholder')}
              disabled={loading}
              autoComplete="username"
              autoFocus
            />
          </div>

          <div className={styles.field}>
            <Input
              type="password"
              label={t('auth.password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('auth.passwordPlaceholder')}
              disabled={loading}
              autoComplete="current-password"
            />
          </div>

          <div className={styles.actions}>
            <Button type="submit" disabled={loading} loading={loading}>
              {t('auth.loginButton')}
            </Button>
          </div>
        </form>

        <div className={styles.hint}>
          <p className={styles.hintText}>{t('auth.loginDescription')}</p>
        </div>
      </div>
    </div>
  );
}
